// FORMALIDADES (2026-08-24) — vista dentro de Contratos: uma linha por
// contrato com as etapas da formalização, na ordem do fluxo:
//
//   Venda formalizada → Instrumento no Jurídico → ART → (execução) → Atestado/CAT
//
// Pedido do Lucas: um lugar só para as formalidades, sem se perder na gestão
// operacional — consultar cláusula rápido (o 📄 abre o PDF do instrumento
// direto da linha), gerir ARTs por RT (filtro) e puxar a emissão de atestados.
//
// A tela NÃO grava nada: cada célula abre o contrato na aba onde a coisa se
// resolve (mesmo mecanismo do ComercialAtestados: sessionStorage `op:aba:<id>`
// + onAbrir). As contagens do funil vêm prontas do backend, da MESMA lista da
// tabela — somar aqui de novo é como duas verdades divergem.
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  App as AntApp, Typography, Tag, Table, Button, Input, Select, Space, Spin,
  Tooltip, Empty, Flex,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined, FilePdfOutlined, ReloadOutlined } from '@ant-design/icons';
import { API_URL } from '../config';
import { getToken, fmtData } from './operacional-contratos/utils';
import { STATUS_CONTRATO } from './operacional-contratos/types';
import { statusCfg } from './operacional-contratos/atestadoStatus';
import { abrirPdfDoProjeto } from './juridico-contratos/api';
import { ModalIncluirAssinado } from './juridico-contratos/ModalIncluirAssinado';
import { usePerms } from '../hooks/usePerms';
import { ModalCompatibilidade } from './operacional-contratos/ModalCompatibilidade';

// ── Tipos (espelham handlers/formalidades.js) ───────────────────────────────
interface LinhaFormalidade {
  id: string; numero: string; titulo: string; status: string;
  /** Esta pessoa é gestor/coordenador/responsável ATUAL do contrato, ou está na
   *  equipe dele. Vem do servidor com a mesma régua do gate — é o que impede o
   *  botão de anexar de aparecer na carteira inteira. */
  sou_da_equipe?: boolean;
  contrato_pai_id: string | null;
  gestor_email: string | null; coordenador_email: string | null;
  cliente_nome: string | null;
  venda_numero: string | null;
  venda_aceite_em: string | null; venda_assinado_em: string | null;
  formalizacao: { via: string; ok: boolean; em?: string | null };
  instrumento_arquivado: boolean;
  juridico_situacao: string | null; vigencia_fim: string | null;
  juridico_documento_id: number | null;
  // GUARDA-CHUVA (2026-08-25): já chegavam do backend pelo spread do SELECT;
  // agora estão declarados porque a tela passou a usá-los (botão de anexar).
  cliente_id: number | null;
  juridico_id: number | null;
  coberto_por_instrumento: boolean;
  valor_contratado: string | number | null;
  arts_ativas: number; arts_com_pdf: number; arts_dispensadas: number;
  arts_rts: string[] | null; arts_numeros: string[] | null;
  atestados_qtd: number; atestado_status: string | null; cat_numero: string | null;
  etapa: string;
}

// Etapas do funil, na ordem do fluxo. `divida: false` = não é pendência, é
// oportunidade (contrato concluído sem CAT não está "errado").
const ETAPAS: { key: string; label: string; cor: string; bg: string; dica: string; divida: boolean }[] = [
  { key: 'venda',       label: 'Venda sem formalizar',  cor: '#B91626', bg: '#FDECEC', divida: true,
    dica: 'Sem aceite no link, sem assinatura e sem contrato externo anexado' },
  { key: 'instrumento', label: 'Fora do Jurídico',      cor: '#B07500', bg: '#FFF8E8', divida: true,
    dica: 'Venda formalizada, mas o contrato assinado não está arquivado no Jurídico' },
  { key: 'art',         label: 'ART pendente',          cor: '#B07500', bg: '#FFF8E8', divida: true,
    dica: 'Sem ART cadastrada, ou com ART sem o PDF emitido anexado' },
  { key: 'execucao',    label: 'Em execução',           cor: '#0930D0', bg: '#EFF3FF', divida: false,
    dica: 'Formalidades em dia — contrato rodando' },
  { key: 'atestado',    label: 'Concluído sem CAT',     cor: '#7C3AED', bg: '#F5EDFF', divida: false,
    dica: 'Oportunidade de acervo técnico: contrato encerrado sem atestado timbrado' },
  { key: 'completo',    label: 'Completo',              cor: '#0a7f4a', bg: '#E8F5EE', divida: false,
    dica: 'Venda, instrumento, ART e CAT — tudo fechado' },
];
const ETAPA_MAP = Object.fromEntries(ETAPAS.map(e => [e.key, e]));

const VIA_VIS: Record<string, { label: string; cor: string }> = {
  aceite_link:      { label: 'Aceite no link',      cor: 'green' },
  assinatura:       { label: 'Assinada (D4 Sign)',  cor: 'green' },
  contrato_externo: { label: 'Contrato do cliente', cor: 'green' },
  // GUARDA-CHUVA (2026-08-25): formalizada pelo instrumento que cobre o
  // contrato — o aditivo da Concórdia nunca terá aceite próprio.
  coberto:          { label: 'Coberto por contrato', cor: 'green' },
  aguardando:       { label: 'Aguardando assinatura', cor: 'orange' },
  pendente:         { label: 'Pendente',            cor: 'red' },
  sem_venda:        { label: 'Sem documento',       cor: 'red' },
};

export default function OperacionalFormalidades({ onAbrir }: {
  // Abre o contrato no detalhe. A aba é combinada por sessionStorage
  // `op:aba:<id>` ANTES de chamar — é como ContratoDetalhe escolhe a inicial.
  onAbrir: (contratoId: string) => void;
}) {
  return <AntApp><FormalidadesInner onAbrir={onAbrir} /></AntApp>;
}

function FormalidadesInner({ onAbrir }: { onAbrir: (id: string) => void }) {
  const { message } = AntApp.useApp();
  const [linhas, setLinhas] = useState<LinhaFormalidade[]>([]);
  const [funil, setFunil] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [filtroEtapa, setFiltroEtapa] = useState<string | null>(null);
  const [filtroRt, setFiltroRt] = useState<string | null>(null);
  const [soPais, setSoPais] = useState(false);
  // GUARDA-CHUVA (2026-08-25): a linha que vai receber o contrato assinado.
  // O modal é o MESMO da Visão geral e do Jurídico — uma porta, três lugares.
  const [anexarEm, setAnexarEm] = useState<LinhaFormalidade | null>(null);
  // COMPATIBILIDADE (2026-08-27): a linha que está sendo conferida no modal.
  const [conferir, setConferir] = useState<LinhaFormalidade | null>(null);
  const { pode } = usePerms();
  // 🚨 Gate por LINHA (2026-09-07). Antes era só esta permissão GLOBAL — e esta
  // vista lista a carteira INTEIRA, sem filtro por usuário. Com a permissão
  // nova (`juridico.contratos.acervar`) no coordenador, o botão apareceria em
  // toda linha de todo cliente, que é o oposto da decisão do Lucas ("o contrato
  // DAQUELE projeto"). Quem tem `criar` (admin, diretor) segue vendo em todas —
  // é quem responde pelo acervo jurídico; para os demais vale `sou_da_equipe`,
  // calculado no servidor com a mesma régua do gate.
  const podeCriarFicha = pode('juridico.contratos.criar');
  const podeAcervar = podeCriarFicha || pode('juridico.contratos.acervar');
  const podeAnexarNaLinha = (l: LinhaFormalidade) =>
    podeCriarFicha || (podeAcervar && !!l.sou_da_equipe);

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_formalidades_listar' },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setLinhas(d?.contratos || []);
      setFunil(d?.funil || {});
    } catch (e: any) {
      message.error('Erro ao carregar: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }
  useEffect(() => { carregar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // Filtros no cliente: a lista inteira já veio (uma chamada), e filtrar aqui
  // deixa os cards do funil estáveis — eles contam o TODO, não o filtrado.
  const rts = useMemo(() => {
    // Array.from em vez de spread: o tsconfig do CRA está abaixo de es2015 e
    // não itera Set por spread (TS2802).
    const s = new Set<string>();
    for (const l of linhas) for (const rt of (l.arts_rts || [])) s.add(rt);
    return Array.from(s).sort();
  }, [linhas]);

  const visiveis = useMemo(() => linhas.filter(l => {
    if (filtroEtapa && l.etapa !== filtroEtapa) return false;
    if (filtroRt && !(l.arts_rts || []).includes(filtroRt)) return false;
    if (soPais && l.contrato_pai_id) return false;
    if (busca) {
      const b = busca.toLowerCase();
      if (!(`${l.numero} ${l.titulo} ${l.cliente_nome || ''}`.toLowerCase().includes(b))) return false;
    }
    return true;
  }), [linhas, filtroEtapa, filtroRt, soPais, busca]);

  function abrirNaAba(l: LinhaFormalidade, aba: string) {
    try { sessionStorage.setItem(`op:aba:${l.id}`, aba); } catch {}
    onAbrir(l.id);
  }

  async function abrirPdf(l: LinhaFormalidade) {
    if (!l.juridico_documento_id) {
      // Ficha de contrato REDIGIDO (expediente) não tem documento no acervo —
      // o PDF mora no expediente. Encaminha pra tela do Jurídico.
      message.info('Este instrumento foi redigido internamente — abra pelo módulo Jurídico.');
      return;
    }
    try {
      // Porta do PROJETO: a do acervo responde 404 para a categoria restrita,
      // e o coordenador que este PR passou a atender não abriria nada — nesta
      // tela, que é justamente onde o problema foi visto.
      const url = await abrirPdfDoProjeto(l.id);
      window.open(url, '_blank', 'noopener');
    } catch (e: any) {
      message.error('Não consegui abrir o PDF: ' + (e.response?.data?.error || e.message));
    }
  }

  const colunas: ColumnsType<LinhaFormalidade> = [
    { title: 'Contrato', key: 'contrato', width: 210, fixed: 'left',
      sorter: (a, b) => a.numero.localeCompare(b.numero),
      render: (_, l) => (
        <div style={{ cursor: 'pointer' }} onClick={() => abrirNaAba(l, 'visao')}>
          <Typography.Text strong style={{ fontFamily: 'monospace', color: '#020D7F' }}>
            {l.numero}
          </Typography.Text>
          {l.contrato_pai_id && <Tag style={{ marginLeft: 6, fontSize: 10 }} color="gold">aditivo</Tag>}
          <div style={{ fontSize: 11, color: '#888', whiteSpace: 'nowrap',
            overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 190 }}>
            {l.titulo}
          </div>
          <div style={{ fontSize: 10.5, color: '#aaa', whiteSpace: 'nowrap',
            overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 190 }}>
            {l.cliente_nome || '—'}
          </div>
        </div>
      ) },
    { title: 'Status', key: 'status', width: 110,
      filters: Object.entries(STATUS_CONTRATO).map(([k, v]) => ({ text: v.label, value: k })),
      onFilter: (v, l) => l.status === v,
      render: (_, l) => {
        const s = (STATUS_CONTRATO as any)[l.status];
        return s
          ? <Tag style={{ background: s.bg, color: s.cor, border: 'none', fontWeight: 600 }}>{s.label}</Tag>
          : <Tag>{l.status}</Tag>;
      } },
    // ── 1. Venda ──
    { title: '1 · Venda', key: 'venda', width: 165,
      render: (_, l) => {
        const via = VIA_VIS[l.formalizacao?.via || 'pendente'] || VIA_VIS.pendente;
        const em = l.formalizacao?.em ? fmtData(l.formalizacao.em) : null;
        return (
          <div style={{ cursor: 'pointer' }} onClick={() => abrirNaAba(l, 'visao')}>
            <Tag color={via.cor}>{via.label}</Tag>
            <div style={{ fontSize: 10.5, color: '#888' }}>
              {l.venda_numero || ''}{em ? ` · ${em}` : ''}
            </div>
          </div>
        );
      } },
    // ── 2. Instrumento no Jurídico ──
    { title: '2 · Instrumento', key: 'instrumento', width: 175,
      render: (_, l) => {
        if (!l.instrumento_arquivado) {
          return (
            <div style={{ cursor: 'pointer' }} onClick={() => abrirNaAba(l, 'visao')}>
              <Tag color={l.formalizacao?.ok ? 'orange' : 'default'}>
                {l.formalizacao?.ok ? 'Fora do acervo' : '—'}
              </Tag>
              {/* 🚨 O MOTIVO, derivado da VIA (2026-09-07). "1 · Venda:
                  assinado" ao lado de "2 · Instrumento: Fora do acervo" é o que
                  o Lucas leu como contradição — as duas estão certas, mas a
                  segunda não dizia por quê. E o texto tem que sair da via: com
                  aceite pelo link não existe papel assinado nenhum para
                  arquivar, e chamar isso de "assinado na D4 Sign" seria falso. */}
              {l.formalizacao?.ok && (
                <div style={{ fontSize: 10.5, color: '#888', lineHeight: 1.3 }}>
                  {l.formalizacao.via === 'assinatura'
                    ? 'assinado na D4 Sign — o papel ainda não subiu ao acervo'
                    : l.formalizacao.via === 'contrato_externo'
                      ? 'contrato do cliente recebido — ainda não subiu ao acervo'
                      : l.formalizacao.via === 'aceite_link'
                        ? 'aceite pelo link — não há papel assinado'
                        : 'coberto por outro instrumento'}
                </div>
              )}
              {/* GUARDA-CHUVA (2026-08-25): "onde eu insiro o contrato
                  assinado?" era pergunta legítima — a entrada existia só na
                  Visão geral do contrato, e esta tela apontava para lá com
                  um texto morto. Agora o papel entra daqui mesmo. */}
              {/* Só para quem pode criar ficha no Jurídico: sem a permissão o
                  arquivamento morre em 403 DEPOIS do PDF já ter subido ao S3. */}
              {podeAnexarNaLinha(l) && (
                <div>
                  <Button size="small" type="link" style={{ padding: 0, height: 18, fontSize: 11 }}
                    onClick={(e) => { e.stopPropagation(); setAnexarEm(l); }}>
                    anexar contrato assinado
                  </Button>
                </div>
              )}
            </div>
          );
        }
        return (
          <Space size={4}>
            <div style={{ cursor: 'pointer' }} onClick={() => abrirNaAba(l, 'visao')}>
              <Tag color="green">No Jurídico</Tag>
              <div style={{ fontSize: 10.5, color: '#888' }}>
                {l.vigencia_fim ? `vigência até ${fmtData(l.vigencia_fim)}` : (l.juridico_situacao || '')}
              </div>
            </div>
            <Tooltip title="Abrir o PDF do contrato — pra conferir cláusula sem caçar o arquivo">
              <Button size="small" type="text" icon={<FilePdfOutlined />}
                onClick={(e) => { e.stopPropagation(); abrirPdf(l); }} />
            </Tooltip>
          </Space>
        );
      } },
    // ── 3. ART ──
    { title: '3 · ART', key: 'art', width: 170,
      render: (_, l) => {
        const ativas = Number(l.arts_ativas), comPdf = Number(l.arts_com_pdf),
              disp = Number(l.arts_dispensadas);
        let tag;
        if (ativas === 0 && disp > 0) tag = <Tag>Não se aplica</Tag>;
        else if (ativas === 0) tag = <Tag color="red">Sem ART</Tag>;
        else if (comPdf === 0) tag = <Tag color="orange">{ativas} sem PDF</Tag>;
        else tag = <Tag color="green">{comPdf}/{ativas} com PDF</Tag>;
        const rts = (l.arts_rts || []).map(r => r.split('@')[0]).join(', ');
        return (
          <div style={{ cursor: 'pointer' }} onClick={() => abrirNaAba(l, 'arts')}>
            {tag}
            {rts && <div style={{ fontSize: 10.5, color: '#888' }}>RT: {rts}</div>}
          </div>
        );
      } },
    // ── 4. Atestado / CAT ──
    { title: '4 · Atestado', key: 'atestado', width: 175,
      render: (_, l) => {
        // Number(): COUNT do Postgres chega como STRING pelo node-pg, e "0" é
        // truthy — sem isto, contrato sem atestado caía no ramo de baixo e
        // statusCfg(null) pintava "Backlog" em tudo (achado da review).
        if (!Number(l.atestados_qtd)) {
          // Sem atestado nenhum: só vira chamada quando o contrato terminou.
          return (
            <div style={{ cursor: 'pointer' }} onClick={() => abrirNaAba(l, 'atestados')}>
              {l.status === 'concluido'
                ? <Tag color="purple">Emitir?</Tag>
                : <span style={{ color: '#ccc' }}>—</span>}
            </div>
          );
        }
        const cfg = statusCfg(l.atestado_status);
        return (
          <div style={{ cursor: 'pointer' }} onClick={() => abrirNaAba(l, 'atestados')}>
            <Tag color={cfg.tagColor}>{cfg.label}</Tag>
            {l.cat_numero && <div style={{ fontSize: 10.5, color: '#888' }}>CAT {l.cat_numero}</div>}
          </div>
        );
      } },
    // ── Etapa consolidada ──
    { title: 'Etapa', key: 'etapa', width: 160,
      sorter: (a, b) => ETAPAS.findIndex(e => e.key === a.etapa) - ETAPAS.findIndex(e => e.key === b.etapa),
      render: (_, l) => {
        const e = ETAPA_MAP[l.etapa];
        return e
          ? <Tooltip title={e.dica}><Tag style={{ background: e.bg, color: e.cor, border: 'none', fontWeight: 600 }}>{e.label}</Tag></Tooltip>
          : <Tag>{l.etapa}</Tag>;
      } },
    // ── Conferir: venda × instrumento × ART × atestado lado a lado ──
    // Pedido do Lucas: um modal simplificado por contrato para checar a
    // compatibilidade entre os documentos. Leitura pura.
    { title: '', key: 'conferir', width: 44, fixed: 'right',
      render: (_, l) => (
        <Tooltip title="Conferir venda × instrumento × ART × atestado">
          <Button size="small" type="text" icon={<SearchOutlined />}
            onClick={(e) => { e.stopPropagation(); setConferir(l); }} />
        </Tooltip>
      ) },
  ];

  return (
    <div style={{ padding: '12px 24px 24px' }}>
      {/* ── O funil: o fluxograma do processo, com contagem ── */}
      <Flex gap={10} wrap="wrap" style={{ marginBottom: 14 }}>
        {ETAPAS.map((e, i) => (
          <div key={e.key}
            onClick={() => setFiltroEtapa(filtroEtapa === e.key ? null : e.key)}
            style={{
              cursor: 'pointer', padding: '8px 14px', borderRadius: 8, minWidth: 128,
              background: e.bg, border: filtroEtapa === e.key
                ? `2px solid ${e.cor}` : '2px solid transparent',
              position: 'relative',
            }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: e.cor }}>{funil[e.key] || 0}</div>
            <div style={{ fontSize: 11, color: '#555' }}>{i + 1 <= 3 ? `${i + 1}. ` : ''}{e.label}</div>
            {i < ETAPAS.length - 1 && (
              <span style={{ position: 'absolute', right: -11, top: '38%', color: '#bbb', fontSize: 14 }}>→</span>
            )}
          </div>
        ))}
      </Flex>

      {/* ── Filtros ── */}
      <Flex gap={8} wrap="wrap" style={{ marginBottom: 12 }} align="center">
        <Input prefix={<SearchOutlined />} placeholder="Contrato, título ou cliente"
          allowClear value={busca} onChange={e => setBusca(e.target.value)}
          style={{ width: 260 }} />
        <Select allowClear placeholder="RT (profissional da ART)" style={{ minWidth: 220 }}
          value={filtroRt} onChange={setFiltroRt} showSearch
          options={rts.map(r => ({ value: r, label: r }))} />
        <Select value={soPais ? 'pais' : 'todos'} style={{ width: 170 }}
          onChange={v => setSoPais(v === 'pais')}
          options={[
            { value: 'todos', label: 'Com aditivos' },
            { value: 'pais', label: 'Só contratos-pai' },
          ]} />
        <div style={{ flex: 1 }} />
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {visiveis.length} de {linhas.length} contrato(s)
        </Typography.Text>
        <Button icon={<ReloadOutlined />} onClick={carregar} loading={loading} />
      </Flex>

      {loading && !linhas.length ? (
        <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>
      ) : (
        <Table<LinhaFormalidade>
          size="small" rowKey="id" columns={colunas} dataSource={visiveis}
          pagination={{ pageSize: 50, showSizeChanger: false }}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: <Empty description="Nenhum contrato no filtro" /> }}
        />
      )}

      {/* O papel entra por aqui: mesmo modal da Visão geral, com o contrato
          da linha já marcado na cobertura. Ao arquivar, a lista recarrega —
          a linha muda de etapa sozinha, sem a pessoa procurar onde olhar. */}
      <ModalCompatibilidade
        contratoId={conferir?.id || null}
        numero={conferir?.numero}
        onFechar={() => setConferir(null)}
        onIrPara={(aba) => { if (conferir) { const l = conferir; setConferir(null); abrirNaAba(l, aba); } }}
      />

      <ModalIncluirAssinado
        // `key` por linha: o modal guarda a fila de PDFs em estado interno e só
        // se limpa no botão Fechar. Como aqui ele fecha ao arquivar (para a
        // lista recarregar), sem remontar a segunda linha abriria com a fila da
        // primeira — e SEM área para soltar o arquivo.
        key={anexarEm?.id || 'nenhum'}
        aberto={!!anexarEm}
        onFechar={() => setAnexarEm(null)}
        onArquivado={() => { setAnexarEm(null); carregar(); }}
        venda={anexarEm ? {
          contrato_op_id: anexarEm.id,
          numero: anexarEm.numero,
          titulo: anexarEm.titulo,
          cliente_id: anexarEm.cliente_id,
          cliente_nome: anexarEm.cliente_nome,
          // Sem isto o Valor da ficha vira o que a IA leu no PDF, em vez do
          // valor contratado que já está acordado no sistema.
          valor_contratado: anexarEm.valor_contratado,
        } : undefined}
      />
    </div>
  );
}
